import type { Partner } from "@expert/shared-types";

import { getC360ApiClient } from "./c360ApiClient";

export interface C360Interaction {
    interactionId: string;
    interactionLineId: string;
}

const US_PHONE_NUMBER = /^\+1(\d{10})$/;

/** Splits a `+1` number into C360's 10-digit caller number and country code; any other format is left out. */
export function toC360CallerNumber(
    phoneNumber: string | undefined,
): { callerNumber: string; callerCountryCallingCode: string } | Record<string, never> {
    const nationalNumber = phoneNumber && US_PHONE_NUMBER.exec(phoneNumber)?.[1];
    return nationalNumber ? { callerNumber: nationalNumber, callerCountryCallingCode: "1" } : {};
}

const C360_SUMMARY_MAX_LENGTH = 4000;

/** C360 accepts 1–4000 characters, so a longer summary is cut and an empty one left out. */
export function toC360Summary(summary: string | undefined): { summary?: string } {
    const trimmed = summary?.trim();
    return trimmed ? { summary: trimmed.slice(0, C360_SUMMARY_MAX_LENGTH) } : {};
}

const C360_NAME_MAX_LENGTH = 100;

/**
 * Splits the confirmed full name into C360's first and last name. The last word is the last name, so a two-word first
 * name ("Mary Ann Smith") stays whole; a single word is only a first name. C360 accepts 1–100 characters per name,
 * so a longer one is left out rather than failing the link.
 */
export function toC360CustomerName(fullName: string | undefined): { firstName?: string; lastName?: string } {
    const words = fullName?.trim().split(/\s+/).filter(Boolean) ?? [];
    const firstName = (words.length > 1 ? words.slice(0, -1) : words).join(" ");
    const lastName = words.length > 1 ? words.at(-1)! : "";
    const fits = (name: string) => name.length > 0 && name.length <= C360_NAME_MAX_LENGTH;
    return { ...(fits(firstName) ? { firstName } : {}), ...(fits(lastName) ? { lastName } : {}) };
}

/** Starts a C360 interaction for the session, with the number the call came from as C360's `caller_number`. */
export function createInteraction({
    sessionId,
    partner,
    mdn,
}: {
    sessionId: string;
    partner: Partner;
    mdn?: string;
}): Promise<C360Interaction> {
    return getC360ApiClient(sessionId)
        .post("v1/interactions", { json: { partner, sessionId, ...toC360CallerNumber(mdn) } })
        .json<C360Interaction>();
}

/** Adds Expert Workspace's line to an interaction another channel started, such as the IVR. */
export function joinInteraction({
    sessionId,
    partner,
    interactionId,
}: {
    sessionId: string;
    partner: Partner;
    interactionId: string;
}): Promise<C360Interaction> {
    return getC360ApiClient(sessionId)
        .post(`v1/interactions/${encodeURIComponent(interactionId)}/lines`, {
            json: { partner, sessionId },
        })
        .json<C360Interaction>();
}

/** Closes our line on the interaction, with the session summary. The service sets the line's end time. */
export async function wrapInteractionLine({
    sessionId,
    partner,
    interactionLineId,
    summary,
}: {
    sessionId: string;
    partner: Partner;
    interactionLineId: string;
    summary?: string;
}): Promise<void> {
    await getC360ApiClient(sessionId).post(`v1/lines/${encodeURIComponent(interactionLineId)}/wrap`, {
        json: { partner, ...toC360Summary(summary) },
    });
}

const US_MDN = /^(?:\+1)?(\d{10})$/;

/** The 10 digits C360's customer search takes, from a `+1` number or one the expert typed; undefined otherwise. */
export function toC360Mdn(mdn: string | undefined): string | undefined {
    return (mdn && US_MDN.exec(mdn)?.[1]) || undefined;
}

export interface C360HistoryInteraction {
    interactionId: string;
    channel: string | null;
    status: string | null;
    startTime: string | null;
    endTime: string | null;
    durationSeconds: number | null;
    summary: string | null;
}

export interface C360CustomerLink {
    /** `linked`: a subscription on the partner's client channel is now on the line. */
    outcome: "linked" | "no_subscriptions";
    subscriptionCount: number;
    /** False when some history is missing: either a subscription's history fetch failed, or C360 cut the customer search short and more subscriptions may exist. */
    historyComplete: boolean;
    /** The latest interactions across the customer's subscriptions on the channel, newest first. */
    interactions: C360HistoryInteraction[];
}

/**
 * Looks the number up in C360, links the line to the customer's subscription on the partner's channel, with the
 * caller's name, and returns the history across their subscriptions there when `includeHistory` is set: each
 * subscription's history is a C360 model call, so it's only asked for when something will show it.
 */
export function linkCustomerAndGetHistory({
    sessionId,
    partner,
    interactionLineId,
    mdn,
    customerName,
    includeHistory,
}: {
    sessionId: string;
    partner: Partner;
    interactionLineId: string;
    mdn: string;
    customerName?: string;
    includeHistory: boolean;
}): Promise<C360CustomerLink> {
    return getC360ApiClient(sessionId)
        .post(`v1/lines/${encodeURIComponent(interactionLineId)}/customer-link`, {
            json: { partner, mdn, ...toC360CustomerName(customerName), includeHistory },
        })
        .json<C360CustomerLink>();
}
